import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { BehaviorSubject, combineLatest, finalize } from "rxjs";
import { AuthenticationMethod, IntegrationModel, IntegrationToAddModel, Platform } from "src/app/models/api";
import { IntegrationCreationResponseModel, IntegrationCredentials, StatusCodeEnum } from "src/app/models/api/integration.model";
import { IIntegrationComponentInput, IIntegrationComponentOutput } from "src/app/models/componentInputs/integrationComponentInput.interface";
import { CredentialKey, SecretCredentialsKeys } from "src/app/models/constants/credentials.constants";
import { FeatureFlagTypes } from "src/app/models/featureFlag/featureFalgsTypes.enum";
import { ISelectOption, PlatformSelectOptions } from "src/app/models/selectOptions";
import { IDetailKeyValue } from "src/app/models/singlePageComponents";
import { LabelsProviderPipe } from "src/app/pipes/labels-provider/labels-provider.pipe";
import { RequiredFieldTitlePipe } from "src/app/pipes/required-field-title/required-field-title.pipe";
import { HttpErrorHandlerService } from "src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service";
import { IntegrationsHttpService } from "src/app/services/httpServices/integrationsHttpsService/integrations-http.service";
import { FeatureFlagsService } from "src/app/services/infra/featureFlags/feature-flags.service";
import { integrationIsFailing, integrationIsNotFailing } from "./utils";
import { CredentialValidatorsFunctions, RequiredValidatorFunction } from "src/app/components/integration/integration-credentials/consts";
@Component({
    selector: "app-integration",
    templateUrl: "./integration.component.html",
    styleUrls: ["./integration.component.scss"],
})
export class IntegrationComponent implements OnInit {
    public integrationWasUpdated = false;
    public showNoData = false;
    public formSubmitAttempt = false; // using non-primitive boolean to pass by ref
    public shouldUpdateCredentials = false;
    public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public parentIsLoading: BehaviorSubject<boolean>;
    public authMethodHasChanged = false;

    public integrationStatusCodeEnum = StatusCodeEnum;
    public authenticationMethod = AuthenticationMethod;

    // Empty integration Id is equal to in Add mode.
    public integrationId?: string;
    public integration: IntegrationModel = new IntegrationModel();
    public integrationCredentials: IntegrationCredentials;
    public actionsOptions: ISelectOption[] = [];
    public integrationNameDetail: IDetailKeyValue;

    public integrationFormGroup: FormGroup<{
        name: FormControl<string>;
        platform: FormControl<string>;
        environment: FormControl<string>;
        authMethod: FormControl<string>;
        credentials: UntypedFormGroup;
    }>;
    public credentialsFormGroup: UntypedFormGroup = new UntypedFormGroup({});

    public errorMessage: BehaviorSubject<string> = new BehaviorSubject<string>("");

    private _integrationInitialName: string;

    constructor(
        public deleteConnectionDialog: MatDialog,
        private _fb: FormBuilder,
        private _labelsProviderPipe: LabelsProviderPipe,
        private _requiredFieldPipe: RequiredFieldTitlePipe,
        public dialogRef: MatDialogRef<IntegrationComponent>,
        @Inject(MAT_DIALOG_DATA) input: IIntegrationComponentInput,
        private _integrationsHttpService: IntegrationsHttpService,
        private _httpErrorHandlerService: HttpErrorHandlerService,
        private _featureFlagService: FeatureFlagsService,
    ) {
        this.integrationId = input.integrationId;
        this.parentIsLoading = input.parentIsLoading;

        this.dialogRef.disableClose = true;
        this.dialogRef.backdropClick().subscribe(() => {
            this.closeWindow();
        });

        if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.Salesforce)) {
            const salesforceOption = PlatformSelectOptions.filter((option) => Platform.salesforce == (option.id as Platform))[0];
            salesforceOption.disabled = true;
        }

        if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.ServiceNow)) {
            const serviceNowOption = PlatformSelectOptions.filter((option) => Platform.serviceNow == (option.id as Platform))[0];
            serviceNowOption.disabled = true;
        }

        if (!this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.ChatGptEnterprise)) {
            const chatGptEnterpriseOption = PlatformSelectOptions.filter((option) => Platform.chatGptEnterprise == (option.id as Platform))[0];
            chatGptEnterpriseOption.disabled = true;
        }
    }

    public ngOnInit(): void {
        this._initSinglePage();
    }

    public onAddIntegration() {
        const input: IntegrationToAddModel = {
            name: this.integrationFormGroup.controls.name.value,
            description: "",
            platform: this.integrationFormGroup.controls.platform.value as Platform,
            authenticationMethod: this.integrationFormGroup.controls.authMethod.value as AuthenticationMethod,
            environment: this.integrationFormGroup.controls.environment.value,
            credentials: this.credentialsFormGroup.value,
        };
        this.isLoading$.next(true);
        this._integrationsHttpService.addIntegration(input).subscribe({
            next: (response: IntegrationCreationResponseModel) => {
                this.integrationWasUpdated = true;
                this.integrationId = response.integrationId;
                this._initSinglePage(true);
            },
            error: (error) => {
                this._httpErrorHandlerService.handle(error);
                this.isLoading$.next(false);
            },
        });
    }

    public onCancel() {
        // if canceled on Add mode - nothing to update
        // if in edit mode - there can be some updates
        // such as in case title was updated successfully but integration fail on credentials
        // in that case the pop up keeps open and user can press on cancel
        this.closeWindow(this.integrationId ? this.integrationWasUpdated : false);
    }

    public closeWindow(refreshNeeded = false) {
        const doRefresh = this.integrationId ? this.integrationWasUpdated : false || refreshNeeded;
        const output = {
            refreshNeeded: doRefresh,
            integrationId: this.integrationId,
        } as IIntegrationComponentOutput;
        this.dialogRef.close(output);
    }

    public onUpdate() {
        const shouldUpdateName = this._integrationInitialName !== this.integrationFormGroup.controls.name.value;
        this.shouldUpdateCredentials = this._shouldUpdateCredentials();

        if (!(shouldUpdateName || this.shouldUpdateCredentials)) {
            this.closeWindow(false);
        }

        if (shouldUpdateName) {
            this.isLoading$.next(true);
            this._integrationsHttpService
                .editIntegration(this.integration.id, {
                    name: this.integrationFormGroup.controls.name.value,
                })
                .subscribe({
                    next: () => {
                        this.integrationWasUpdated = true;
                        // if credentials are the same - no need to update them
                        if (!this.shouldUpdateCredentials) {
                            this.closeWindow(true);
                        } else {
                            this._onEditIntegrationAuthentication();
                        }
                    },
                    error: (error) => {
                        this.isLoading$.next(false);
                        this.shouldUpdateCredentials = false;
                        this.errorMessage.next(this._labelsProviderPipe.transform("integrationPage.errorMessageOnUpdate"));
                        this._httpErrorHandlerService.handle(error);
                    },
                });
        } else {
            this._onEditIntegrationAuthentication();
        }
    }

    private _onEditIntegrationAuthentication() {
        if (this.authMethodHasChanged) {
            this._editIntegrationAuthAndCredentials();
        } else {
            this._editIntegrationCredentials();
        }
    }

    private _initSinglePage(closeDialogOnSuccess = false) {
        if (this.integrationId) {
            this.isLoading$.next(true);
            combineLatest({
                integration: this._integrationsHttpService.getIntegration(this.integrationId),
                credentials: this._integrationsHttpService.getIntegrationCredentials(this.integrationId),
            })
                .pipe(finalize(() => this.isLoading$.next(false)))
                .subscribe({
                    next: ({ integration, credentials }) => {
                        this.integration = integration;
                        this.integrationCredentials = credentials;

                        if (closeDialogOnSuccess && integrationIsNotFailing(this.integration)) {
                            this.closeWindow(true);
                        }
                        this._initIntegration();
                    },
                    error: (error) => {
                        this._httpErrorHandlerService.handle(error);
                        if (error.status === 404) {
                            this.showNoData = true;
                        }
                    },
                });
        } else {
            this._initIntegration();
            this.isLoading$.next(false);
        }
    }

    private _initIntegration() {
        if (integrationIsFailing(this.integration) && this.integration.connectionStatus?.errorDisplayMessage) {
            this.errorMessage.next(this.integration.connectionStatus.errorDisplayMessage);
        }

        this.integrationFormGroup = this._fb.group({
            name: new UntypedFormControl(this.integration.name ?? "", [Validators.required]),
            platform: new UntypedFormControl(this.integration.platform ?? "", [Validators.required]),
            environment: new UntypedFormControl(this.integration.environment ?? "", [Validators.required]),
            authMethod: new UntypedFormControl(this.integration.authenticationMethod ?? "", [Validators.required]),
            credentials: this.credentialsFormGroup,
        });

        this._initIntegrationNameInput();

        // Register for change of auth method
        this.integrationFormGroup.controls.authMethod.valueChanges.subscribe(() => {
            this.formSubmitAttempt = false;
            this.authMethodHasChanged = true;
            this.errorMessage.next("");
        });
    }

    private _initIntegrationNameInput() {
        // initial integration name and credentials to be compared on save
        this._integrationInitialName = this.integrationFormGroup.controls.name.value;

        this.integrationNameDetail = {
            key: this._requiredFieldPipe.transform("integrationPage.integrationNameField"),
            formValue: this.integrationFormGroup.controls.name,
        };
    }

    /**
     * This is needed when the user only changes the auth method
     * Because the user changed the auth method he needs to add all the credentials and that is the reason that we handle the credentials like the add integration.
     */
    private _editIntegrationAuthAndCredentials() {
        const credentials = this.credentialsFormGroup.value;
        this.isLoading$.next(true);
        this._integrationsHttpService
            .editIntegrationAuthentication(
                this.integration.id,
                this.integrationFormGroup.controls.authMethod.value as AuthenticationMethod,
                credentials,
            )
            .subscribe({
                next: () => {
                    this.integrationWasUpdated = true;
                    this.shouldUpdateCredentials = false;
                    this._initSinglePage(true);
                },
                error: (error) => {
                    this.shouldUpdateCredentials = false;
                    this.errorMessage.next(this._labelsProviderPipe.transform("integrationPage.errorMessageOnUpdateCredentials"));
                    this._httpErrorHandlerService.handle(error);
                    this.isLoading$.next(false);
                },
            });
    }

    /**
     * This is needed when the user only update a credential from the same auth method.
     * On this specific case the UI hasn't all the credentials because the server doesn't return the secrets.
     */
    private _editIntegrationCredentials() {
        const credentials = this.credentialsFormGroup.value;
        const splittedCredentials = this._splitCredentialsToSecretAndNonSecret(credentials);
        this._integrationsHttpService
            .editIntegrationCredentials(this.integration.id, splittedCredentials.nonSecretCredentials, splittedCredentials.secretCredentials)
            .subscribe({
                next: () => {
                    this.integrationWasUpdated = true;
                    this._initSinglePage(true);
                },
                error: (error) => {
                    this.isLoading$.next(false);
                    this.errorMessage.next(this._labelsProviderPipe.transform("integrationPage.errorMessageOnUpdateCredentials"));
                    this._httpErrorHandlerService.handle(error);
                },
            });
    }

    private _shouldUpdateCredentials() {
        let shouldUpdateCredentials = JSON.stringify(this.integrationCredentials) !== JSON.stringify(this.credentialsFormGroup.value);

        if (this.integrationFormGroup.controls.authMethod.value === AuthenticationMethod.oauthAuthorizationCode) {
            // in edit mode - auth code is not part of the credentials control since its not returned in api
            shouldUpdateCredentials = shouldUpdateCredentials && this.credentialsFormGroup.get(CredentialKey.authCode) !== null;

            if (shouldUpdateCredentials) {
                // add oauth control with validators
                const control = new FormControl<string>(
                    this.integrationCredentials[CredentialKey.authCode] ?? "",
                    RequiredValidatorFunction.concat(CredentialValidatorsFunctions),
                );
                this.credentialsFormGroup.addControl(CredentialKey.authCode, control);
            }
        }

        return shouldUpdateCredentials;
    }

    private _splitCredentialsToSecretAndNonSecret(allCredentials: IntegrationCredentials): {
        secretCredentials: IntegrationCredentials;
        nonSecretCredentials: IntegrationCredentials;
    } {
        const secretCredentials: IntegrationCredentials = {};
        const nonSecretCredentials: IntegrationCredentials = {};

        for (const key in allCredentials) {
            if (allCredentials[key]) {
                if (SecretCredentialsKeys.includes(<CredentialKey>key)) {
                    secretCredentials[key] = allCredentials[key];
                } else {
                    nonSecretCredentials[key] = allCredentials[key];
                }
            }
        }

        return { secretCredentials, nonSecretCredentials };
    }
}
